import React, {FC} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {PaletteColorsEnum} from '../../../../../libs/shared/core/src/models/api/paletteModel';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import schedulingAppearanceSettings from '../../../../assets/images/schedulingAppearanceSettings.png';
import {SchedulingLinkPropertiesEnum} from '../../../../helpers/models/schedulingLinkHelpers';
import {PaletteColorPicker} from '../../../common/color/paletteColorPicker';
import {schedulingLinkPropertyTooltips} from '../schedulingLinkViewHelpers';
import {SchedulingLinkBoxedInputWrapper} from './schedulingLinkBoxedInputWrapper';
import {SchedulingLinkDetailRow} from './schedulingLinkDetailRow';
import {SchedulingLinkSettingsSection} from './schedulingLinkSettingsSection';

/*
 * Props.
 */

export interface SchedulingLinkAppearanceSettingsProps {
  color: PaletteColorsEnum | undefined;
  greeting: string;
  displayName: string;
  onColorChange: (arg: PaletteColorsEnum | undefined) => void;
  onGreetingChange: (arg: string) => void;
  onDisplayNameChange: (arg: string) => void;
}

/*
 * Style.
 */

const StyledRowsWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 20px;
  padding-bottom: 20px;
`;

const StyledSectionSummaryDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
`;

const StyledImage = styled.img`
  width: 200px;
`;

const StyledColorPickerDiv = styled.div`
  display: grid;
  justify-items: start;
`;

/*
 * Component.
 */

export const SchedulingLinkAppearanceSettings: FC<SchedulingLinkAppearanceSettingsProps> = (props) => {
  const localize = useLocalize();

  return (
    <SchedulingLinkSettingsSection
      sectionSummary={
        <StyledSectionSummaryDiv>
          <div>
            <Trans id="schedulinglink-settings-appearance-summary">
              Customize the appearance of the booking page that your recipient will see when viewing this
              scheduling link.
            </Trans>
          </div>
          <StyledImage src={schedulingAppearanceSettings} />
        </StyledSectionSummaryDiv>
      }
    >
      <StyledRowsWrapperDiv>
        {renderColorRow(props)}
        {renderGreetingRow(localize, props)}
        {renderDisplayNameRow(localize, props)}
      </StyledRowsWrapperDiv>
    </SchedulingLinkSettingsSection>
  );
};

function renderColorRow(props: SchedulingLinkAppearanceSettingsProps) {
  return (
    <SchedulingLinkDetailRow label={<Trans id="schedulinglink-settings-color">Color</Trans>}>
      <StyledColorPickerDiv>
        <PaletteColorPicker onColorSelect={props.onColorChange} paletteColor={props.color} />
      </StyledColorPickerDiv>
    </SchedulingLinkDetailRow>
  );
}

function renderGreetingRow(localize: Localizer, props: SchedulingLinkAppearanceSettingsProps) {
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-greeting">Greeting</Trans>}
      renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.GREETING]}
    >
      <SchedulingLinkBoxedInputWrapper>
        <TextBox
          placeholder={localize(
            defineMessage({
              id: 'schedulinglink-settings-greetingplaceholder',
              message: 'Add a custom greeting',
            }),
          )}
          defaultValue={props.greeting}
          onChange={props.onGreetingChange}
        />
      </SchedulingLinkBoxedInputWrapper>
    </SchedulingLinkDetailRow>
  );
}

function renderDisplayNameRow(localize: Localizer, props: SchedulingLinkAppearanceSettingsProps) {
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-displayname">Display name</Trans>}
      renderTooltip={() => schedulingLinkPropertyTooltips[SchedulingLinkPropertiesEnum.DISPLAY_NAME]}
    >
      <SchedulingLinkBoxedInputWrapper>
        <TextBox
          placeholder={localize(
            defineMessage({
              id: 'schedulinglink-settings-displaynameplaceholder',
              message: 'Add a display name',
            }),
          )}
          defaultValue={props.displayName}
          onChange={props.onDisplayNameChange}
        />
      </SchedulingLinkBoxedInputWrapper>
    </SchedulingLinkDetailRow>
  );
}
